import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContextualHelpModal from '../../../../components/ContextualHelpModal';
import InfoLabel from '../../../../components/ContextualHelpModal/InfoLabel';
import LabeledInput from '../../../../components/LabeledInput';
import { DatePicker } from '../../../../components/DatePicker';
import { SelectInput } from '../../../../components/SelectInput';
import { TextInput } from '../../../../components/TextInput';

import * as api from '../../../../api';

import defaultStyles from '../../../../styles/formSetStyles.module.css';
import { sendEventMessage, Message } from '../../../../utils/native';

const NUMBERS_AND_LETTERS_REGEX = /^[A-Za-z0-9]*$/;

interface Props {
  payorOptions: string[];
  previsitPayorDisplayMapping: Record<string, string>;
  cardInformation: Partial<api.PatientInsuranceInformation.InsuranceCardInformation>;
  onChange: (
    updated: Partial<api.PatientInsuranceInformation.InsuranceCardInformation>,
  ) => void;
}

const SUBSCRIBER_HELP_MARKDOWN = `## Finding the correct Subscriber or Member ID

Each insurance card has a unique ID that your insurance provider uses to identify you and any dependents you may have on your plan. It is usually located in the top left section of your card and can be called Member ID, Subscriber ID, or ID. Include all the letters and numbers in your ID, but leave out any dashes, spaces or other symbols.`;

export const InsuranceCardInfo: React.FC<Props> = ({
  payorOptions,
  previsitPayorDisplayMapping,
  cardInformation,
  onChange,
}) => {
  const { t } = useTranslation();
  const [showHelp, setShowHelp] = useState(false);

  const onChangeWithNativeAction = (
    updated: Partial<api.PatientInsuranceInformation.InsuranceCardInformation>,
  ) => {
    onChange(updated);
    sendEventMessage(Message.ActiveAction);
  };
  const label = t('Enter the following information from your insurance card.');

  return (
    <div>
      <ContextualHelpModal
        headerText={t('More information')}
        helpText={t(SUBSCRIBER_HELP_MARKDOWN)}
        isOpen={showHelp}
        onRequestClose={() => setShowHelp(false)}
        onHandleHelpful={async () => setShowHelp(false)}
      />
      <label className={defaultStyles.Label}>{label}</label>
      <LabeledInput
        label={t('Insurance Company')}
        subLabel={t('If you cannot find your payor, please select "Other"')}
        htmlFor="insurance-card-info-payor"
      >
        <SelectInput
          id="insurance-card-info-payor"
          onChange={(payor: string) => {
            onChangeWithNativeAction({
              ...cardInformation,
              payor,
            });
          }}
          options={payorOptions.map((payor) => ({
            display:
              {
                ...api.Payor.payorMap,
                ...previsitPayorDisplayMapping,
              }[payor] || payor,
            key: payor,
            value: payor,
          }))}
          value={cardInformation.payor || ''}
        />
      </LabeledInput>

      {cardInformation.payor && (
        <>
          <LabeledInput
            label={t('Subscriber or Member ID')}
            htmlFor="insurance-card-info-subscriber-id"
          >
            <TextInput
              id="insurance-card-info-subscriber-id"
              value={cardInformation.primaryPolicyHolderMemberId || ''}
              placeholder={t('Include all numbers & letters')}
              onChange={(event) => {
                const valid =
                  NUMBERS_AND_LETTERS_REGEX.exec(event.target.value) !== null;
                if (!valid) return;
                onChangeWithNativeAction({
                  ...cardInformation,
                  primaryPolicyHolderMemberId: event.target.value,
                });
              }}
            />
          </LabeledInput>
          <InfoLabel
            label={t('Where do I find this?')}
            onClick={() => setShowHelp(true)}
          />
          {!api.PatientInsuranceInformation.PAYORS_NO_GROUP_NUMBER.includes(
            cardInformation.payor as api.PatientInsuranceInformation.Payor,
          ) && (
            <LabeledInput
              label={t('Group or plan number')}
              htmlFor="insurance-card-info-group-number"
            >
              <TextInput
                id="insurance-card-info-group-number"
                value={cardInformation.groupNumber || ''}
                placeholder={t('Include all numbers & letters')}
                onChange={(event) => {
                  const valid =
                    NUMBERS_AND_LETTERS_REGEX.exec(event.target.value) !== null;
                  if (!valid) return;
                  onChangeWithNativeAction({
                    ...cardInformation,
                    groupNumber: event.target.value,
                  });
                }}
              />
            </LabeledInput>
          )}
          <LabeledInput
            label={t('coverageStartDate', { ns: 'insurance' })}
            subLabel={t(
              'Fill this out now to save time at your next appointment',
            )}
            htmlFor="insurance-card-info-coverage-start-date"
          >
            <DatePicker
              curvedInput={5}
              date={
                cardInformation.coverageStartDate &&
                !isNaN(new Date(cardInformation.coverageStartDate).valueOf())
                  ? new Date(cardInformation.coverageStartDate).toISOString()
                  : ''
              }
              onChange={(coverageStartDate) =>
                onChangeWithNativeAction({
                  ...cardInformation,
                  coverageStartDate,
                })
              }
            />
          </LabeledInput>
        </>
      )}
    </div>
  );
};
